import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { EP360Tabs } from 'legacy/lut/components/widgets/public/partner360/p360Tabs/models/eP360Tabs'
import { type ReactNode, useState } from 'react'
import { useSelector } from 'react-redux'

import { getUrlWithParams } from 'core-components/helpers/url'
import { TAB_URL } from 'partner360/configs'
import { PLANNING_COCKPIT_TOUR_TARGETS as TOUR_TARGETS } from 'partner360/configs/widgetTour/planningCockpitTourTargets'
import { appendP360TabScrollIntent } from 'partner360/lib/tabScrollIntent'

import { hasPbmSignature } from '../../lib/hasPbmSignature'
import { getCockpitPlanningStatus, useCockpitPlanningStatuses } from '../../model/hooks'
import { selectCockpitCommitmentData, selectCockpitOffering } from '../../model/selectors'
import { CockpitEsp } from '../cockpitEsp'
import { CockpitHelpTools } from '../cockpitHelpPanel'
import type { ICockpitHelpTour } from '../cockpitHelpPanel'
import { CockpitProgramTypes } from '../cockpitProgramTypes/CockpitProgramTypes'
import { useCockpitSectionNavigation } from '../components/cockpitSectionNavigation'

import { CockpitBoardSlide } from './components/cockpitBoardSlide'
import { CockpitOfferingsSwitch } from './components/cockpitOfferingsSwitch'
import { CockpitPlanDeck } from './components/cockpitPlanDeck'
import { OfferingSwitchedOffNote } from './components/cockpitPlanDeck/OfferingSwitchedOffNote'
import { PlanCompletenessDetail } from './components/cockpitPlanDeck/PlanCompletenessDetail'
import { PlanQualityReviewDialog } from './components/cockpitPlanDeck/PlanQualityReviewDialog'
import { CockpitSectionDeck } from './components/cockpitSectionDeck'
import { useCockpitScope } from './hooks/useCockpitScope'

import styles from './CockpitScopePanel.module.scss'

interface ICockpitScopePanelProps {
  partnerId: string
  tour?: ICockpitHelpTour
  /** The board the tabs switch. */
  children: ReactNode
  /** Passes the tray's height down to the board, for a board fitted to the screen. */
  fill?: boolean
  /** A tile of the board is expanded, so the deck folds to give it the height. */
  isTileExpanded?: boolean
}

/** The tray the board lies in. At its head the plan deck holds the year, the programme tabs and
 *  the selected programme's planning state with the scope the numbers are measured in; picking a
 *  tab slides that programme's board in under it. */
export const CockpitScopePanel = ({
  partnerId,
  tour,
  children,
  fill = false,
  isTileExpanded = false,
}: ICockpitScopePanelProps) => {
  const selectedOffering = useSelector(selectCockpitOffering)
  const statuses = useCockpitPlanningStatuses(partnerId)
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  /* The review scores one offering's plan, so `Overall`'s average has none to open. */
  const canReview = Boolean(partnerId) && selectedOffering !== ProgramTypes.Overall
  const { openUrl } = useCockpitSectionNavigation()
  const { isLatestFYear } = useCockpitScope()
  const commitments = useSelector(selectCockpitCommitmentData)
  /* ESP asks for a PBM's signature on a plan of the current fiscal year: the signatures are the
     year in view, so a past year in view answers no. */
  const isPlanSigned = isLatestFYear && hasPbmSignature(commitments, partnerId)

  const deck = {
    status: getCockpitPlanningStatus(statuses, selectedOffering),
    offerings: (
      <CockpitProgramTypes
        partnerId={partnerId}
        embedded
        trailing={<CockpitOfferingsSwitch partnerId={partnerId} />}
      />
    ),
    tools: (
      <>
        <CockpitEsp partnerId={partnerId} isPlanSigned={isPlanSigned} />
        <CockpitHelpTools
          partnerId={partnerId}
          mode="new"
          tour={tour}
          tourId={TOUR_TARGETS.trainingCentre}
          menu
        />
      </>
    ),
    switchedOffNote: <OfferingSwitchedOffNote partnerId={partnerId} />,
    tourTargets: TOUR_TARGETS,
    onQualityClick: canReview ? () => setIsReviewOpen(true) : undefined,
    /* The plan's sections and signature, in a popover under the figures. */
    completenessDetail: <PlanCompletenessDetail />,
    /* The Acknowledgment section, in the window the plan sections open in: its signers and
       history. On Overall the window asks for an offering. */
    onAcknowledgmentClick: () =>
      openUrl(appendP360TabScrollIntent(getUrlWithParams({ [TAB_URL]: EP360Tabs.JbpCommitment }))),
  }

  return (
    <div className={styles.tray}>
      <CockpitPlanDeck {...deck} folded={isTileExpanded} />
      {/* The same deck over an open plan section, whose scrim covers this one. */}
      <CockpitSectionDeck
        deck={deck}
        tabs={<CockpitProgramTypes partnerId={partnerId} embedded />}
      />
      {isReviewOpen && canReview && (
        <PlanQualityReviewDialog
          partnerId={partnerId}
          programType={selectedOffering}
          onClose={() => setIsReviewOpen(false)}
        />
      )}
      <CockpitBoardSlide tileTargets={TOUR_TARGETS} fill={fill}>
        {children}
      </CockpitBoardSlide>
    </div>
  )
}

CockpitScopePanel.displayName = 'CockpitScopePanel'
